import { CallCenterDashboardResponse, featureKeys } from '@/api/callcenter/dashboard';
import { toast } from '@/components/ui/elements/use-toast';
import { useNotificationSound } from '@/features/callcenter/hooks/useNotificationSound';
import { AssignmentBroadcastPayload, broadcastToQueueItem } from '@/features/callcenter/logic';
import { useRealtimeChannelEvents } from '@/hooks/useRealtimeChannelEvents';
import queryClient from '@/lib/react-query';
import { AuthContext } from '@/providers/auth';
import useCallCenterStore from '@/stores/callcenter';
import { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

type NudgeType = NonNullable<AssignmentBroadcastPayload['nudge_type']>;

const NUDGE_COPY: Record<NudgeType, { title: string; body: string }> = {
  accept_expiring: {
    title: 'A lead offer is about to expire',
    body: 'Open it now or it rotates to a teammate.',
  },
  idle: {
    title: 'Still working that lead?',
    body: 'It will rotate soon without activity.',
  },
  sla_warning: {
    title: 'SLA warning',
    body: 'This lead needs a call now.',
  },
};

const NUDGE_TOAST_DURATION_MS = 8000;
const NUDGE_HIGHLIGHT_DURATION_MS = 5000;

// Sibling listener hooks on the ALREADY-SUBSCRIBED user-notifications private
// channel (see useUserNotificationsChannel.ts) -- these are cache-invalidation
// hints, not truth. `GET /dashboard` (via React Query's own poll/refetch)
// remains the source of truth; every handler below follows its optimistic
// write (if any) with an invalidation of the same query.
export const useCallCenterAgentChannel = () => {
  const { user } = useContext(AuthContext);
  const { playAssigned, playNudge } = useNotificationSound();
  const { addRotatedLeadId, removeRotatedLeadId, setHighlightedAssignmentId } = useCallCenterStore(
    useShallow((state) => ({
      addRotatedLeadId: state.addRotatedLeadId,
      removeRotatedLeadId: state.removeRotatedLeadId,
      setHighlightedAssignmentId: state.setHighlightedAssignmentId,
    })),
  );

  const channelId = user?.id ?? user?.user_id;
  const channelName = channelId ? `user-notifications.${channelId}` : '';
  const enabled = Boolean(channelName && user?.is_callcenter);

  useRealtimeChannelEvents<AssignmentBroadcastPayload>({
    kind: 'private',
    channelName,
    enabled,
    events: ['callcenter.lead-assigned', '.callcenter.lead-assigned'],
    handler: (payload) => {
      // Reassignment supersedes any earlier rotated-away flag for this
      // lead: rotation cycles leads among a small pool, so a lead rotated
      // away can come BACK to this agent within the same session, and a
      // stale flag would keep the moved-away banner up -- with viewing
      // pings disabled, inviting a real idle rotation -- on a lead that is
      // genuinely theirs again. Cleared before the duplicate check below so
      // a redelivered reassignment can never resurrect the banner either.
      if (payload.lead?.lead_id) {
        removeRotatedLeadId(payload.lead.lead_id);
      }

      const dashboard = queryClient.getQueryData<CallCenterDashboardResponse>(
        featureKeys.dashboard(),
      );
      const alreadyQueued = Boolean(
        dashboard?.queue.some((item) => item.assignment_id === payload.assignment_id),
      );

      // Duplicate broadcast delivery: the first one already prepended,
      // invalidated, toasted, and chimed -- repeating any of that would
      // double-notify the agent over nothing new.
      if (alreadyQueued) {
        return;
      }

      // The optimistic prepend below also bumps the query's dataUpdatedAt
      // (see useQueueCountdowns for what that anchor drives).
      queryClient.setQueryData<CallCenterDashboardResponse>(featureKeys.dashboard(), (previous) =>
        previous
          ? { ...previous, queue: [broadcastToQueueItem(payload), ...previous.queue] }
          : previous,
      );

      queryClient.invalidateQueries({ queryKey: featureKeys.all });

      toast({
        title: 'New lead assigned',
        description: payload.lead?.name ?? 'Open your queue',
      });

      playAssigned();
    },
  });

  useRealtimeChannelEvents<AssignmentBroadcastPayload>({
    kind: 'private',
    channelName,
    enabled,
    events: ['callcenter.lead-rotated', '.callcenter.lead-rotated'],
    handler: (payload) => {
      if (payload.lead?.lead_id) {
        addRotatedLeadId(payload.lead.lead_id);
      }

      queryClient.invalidateQueries({ queryKey: featureKeys.all });
    },
  });

  // Completion is the one transition with no local mutation behind it: the
  // usual trigger is BrightPattern posting a disposition after the call ends,
  // so without this the finished lead -- and the replacement the server tops
  // the batch up with -- both wait out the dashboard's poll interval.
  useRealtimeChannelEvents<AssignmentBroadcastPayload>({
    kind: 'private',
    channelName,
    enabled,
    events: ['callcenter.lead-completed', '.callcenter.lead-completed'],
    handler: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.all });
    },
  });

  useRealtimeChannelEvents<AssignmentBroadcastPayload>({
    kind: 'private',
    channelName,
    enabled,
    events: ['callcenter.nudge', '.callcenter.nudge'],
    handler: (payload) => {
      const copy = payload.nudge_type ? NUDGE_COPY[payload.nudge_type] : null;

      toast({
        title: copy?.title ?? 'Lead needs attention',
        description: copy?.body,
        duration: NUDGE_TOAST_DURATION_MS,
      });

      const { assignment_id: assignmentId } = payload;
      setHighlightedAssignmentId(assignmentId);

      window.setTimeout(() => {
        // Only clear if nothing newer has taken over the highlight in the
        // meantime -- an unconditional clear here could stomp a second nudge
        // that arrived (and highlighted a different assignment) before this
        // timer fired.
        if (useCallCenterStore.getState().highlightedAssignmentId === assignmentId) {
          setHighlightedAssignmentId(null);
        }
      }, NUDGE_HIGHLIGHT_DURATION_MS);

      playNudge();
    },
  });
};
